import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { showErrorSwal } from '@asamco/utils';
import { DxDateBoxModule, DxSelectBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { BehaviorSubject, combineLatest, forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import { Company } from '../../interfaces/company.interface';
import { UserControlView } from '../../interfaces/user-control.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { AlCompanyService } from '../../services/al-company.service';
import { SchedulerService } from '../../services/scheduler.service';
import { TimezoneUtilsService } from '../../services/timezone-utils.service';
import { UserCompanyService } from '../../services/user-company.service';

@Component({
	selector: 'app-shell-company-configuration',
	templateUrl: './company-configuration.component.html',
	styleUrls: ['./company-configuration.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, FormsModule, ReactiveFormsModule, DxTextBoxModule, AsValidatorDirective, DxDateBoxModule, DxSelectBoxModule, AsyncPipe],
})
export class CompanyConfigurationComponent implements OnInit {
	@Input()
	isCompanyOwner: boolean;

	@Input()
	canManageCompany: boolean;

	companyId: number;

	public iTest: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
	timezones: any[] = [];
	public selectedIndex: string;
	public originalTimezone: string;
	public originalDateFormat: string;
	public selectedDateFormat: string;

	public format: string;

	public dateFormats: string[] = ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy/MM/dd'];

	public formInitialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	formGroup: FormGroup;

	constructor(
		private datepipe: DatePipe,
		private timeZoneCache: TimezoneUtilsService,
		private companyService: AlCompanyService,
		private schedulerService: SchedulerService,
		private selectedCompanyService: SelectedCompanyService,
		private userCompanyService: UserCompanyService
	) {
		//this.createFormGroup();
		this.getLocations();
	}

	ngOnInit(): void {
		this.selectedCompanyService.getCompanyIdObservable().subscribe(e => {
			this.companyId = e;
			forkJoin([this.companyService.getSingleCompany(e), this.userCompanyService.GetUserControlOverview(e)]).subscribe(
				([companyInfo, users]) => {
					this.createFormGroup(companyInfo, users);
				}
			);
		});
	}

	isTimezoneEditingDisabled() {
		if (this.isCompanyOwner) {
			return false;
		} else if (this.canManageCompany) {
			return false;
		}
		return true;
	}

	getLocations() {
		this.iTest.next(this.testTimeZone());
	}

	testTimeZone() {
		return this.timeZoneCache.wrapper(Date.now);
	}

	createFormGroup(company: Company, users: UserControlView[]) {
		//Allow multiple people to be the company owner in case people want that
		let listOfCompanyOwnerIds = users.filter(item => item.isOwner == true).map(a => a.email);
		let companyOwnerString = listOfCompanyOwnerIds.join(';');

		this.format = company.dateFormat;

		let creationDate = company.creationDate ? new Date(company.creationDate) : null;
		if(creationDate && isNaN(creationDate.getTime())){
			creationDate = null;
		}

		this.selectedIndex = company.timezoneString;
		this.originalTimezone = company.timezoneString;
		this.originalDateFormat = company.dateFormat;
		this.selectedDateFormat = company.dateFormat;

		this.formGroup = new FormGroup({
			id: new FormControl({ value: company.id, disabled: true }),
			nameOfCompany: new FormControl({ value: company.name, disabled: true }),
			fullDomainOfCompany: new FormControl({ value: company.fullIdentifier, disabled: true }),
			companyOwner: new FormControl({ value: companyOwnerString, disabled: true }),
			companyCreationDate: new FormControl({ value: creationDate, disabled: true }),
		});

		this.formInitialized.next(true);
	}

	saveForm() {
		if (this.selectedIndex != this.originalTimezone) {
			let obj: any = this.formGroup.getRawValue();
			obj.timezoneString = this.selectedIndex;

			this.schedulerService.checkIfSchedulesAreActiveForCompany(this.companyId).subscribe({
				next: e => {
					this.companyService.updateCompany(obj).subscribe({
						next: e => {
							Swal.fire({ icon: 'success', title: 'Successfully updated company time zone configuration' });
						},
						error: error => showErrorSwal(error)
					});
				},
				error: error => {
					showErrorSwal(error);
				}
			});
		}

		if (this.selectedDateFormat != this.originalDateFormat) {
			let obj: Company = this.formGroup.getRawValue();
			obj.dateFormat = this.selectedDateFormat;
			this.companyService.updateDateTimeFormat(obj).subscribe({
				next: e => {
					Swal.fire({ icon: 'success', title: 'Successfully updated company date format configuration' });
				},
				error: error => showErrorSwal(error)
			});
		}
	}
}
